import React from 'react';
import { ConnectDragPreview, ConnectDragSource, ConnectDropTarget, useDrag, useDrop } from 'react-dnd';
import { useDispatch } from 'react-redux';
import * as Constants from 'const';
import { moveScreen } from 'containers/ScreensPanel/actions';
import { getSpacerPosition } from 'utils/dragAndDrop';

type DraggingItem = {
  position: number;
  screenId: string;
};

type DnDProps = {
  screenId: string;
  panelItemRef: React.RefObject<HTMLDivElement>;
  position: number;
  isOver: boolean;
  toggleOverState: (isOver: boolean) => void;
  updateHotspotPosition: (position: number) => void;
};

const useScreenDrop = ({
  panelItemRef,
  position,
  isOver,
  toggleOverState,
  updateHotspotPosition,
}: Omit<DnDProps, 'screenId'>): ConnectDropTarget => {
  const dispatch = useDispatch();
  const [, drop] = useDrop({
    accept: Constants.DragSourceType.SCREEN,
    hover(_: DraggingItem, monitor) {
      if (!panelItemRef.current) {
        return;
      }
      const boundingClientRect = panelItemRef.current.getBoundingClientRect();
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) {
        return;
      }
      const spacerPosition = getSpacerPosition(boundingClientRect, clientOffset, position);
      if (!isOver) {
        toggleOverState(true);
      }
      updateHotspotPosition(spacerPosition);
    },
    drop(item: DraggingItem, monitor) {
      if (!panelItemRef.current) {
        return;
      }
      const boundingClientRect = panelItemRef.current.getBoundingClientRect();
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) {
        return;
      }
      const spacerPosition = getSpacerPosition(boundingClientRect, clientOffset, position);
      const dragId = item.screenId;
      const dragIndex = item.position;
      const hoverIndex = position;
      if (
        dragIndex === hoverIndex ||
        dragIndex === spacerPosition ||
        dragIndex - spacerPosition === 1
      ) {
        return;
      }
      dispatch(moveScreen(dragId, dragIndex < hoverIndex ? spacerPosition - 1 : spacerPosition));
    },
  });

  return drop;
};

type DragProps = {
  position: number;
  toggleOverState: (isOver: boolean) => void;
  screenId: string;
};

const useScreenDrag = ({
  position,
  toggleOverState,
  screenId,
}: DragProps): [{ isDragging: boolean }, ConnectDragSource, ConnectDragPreview] => {
  return useDrag({
    type: Constants.DragSourceType.SCREEN,
    item: { screenId, position },
    end: () => {
      toggleOverState(false);
    },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });
};

export const useScreenDnD = (
  { screenId, panelItemRef, position, isOver, toggleOverState, updateHotspotPosition }: DnDProps,
): { isDragging: boolean } => {
  const drop = useScreenDrop({ panelItemRef, position, isOver, toggleOverState, updateHotspotPosition });

  const [{ isDragging }, drag] = useScreenDrag({ screenId, position, toggleOverState });

  drag(drop(panelItemRef));

  return { isDragging };
};
